import React, { useEffect, useState } from "react";
import AppLayout from "../components/AppLayout";
import { api } from "../lib/api";
import { useApp } from "../context/AppContext";
import { t } from "../lib/i18n";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Search, Pencil } from "lucide-react";
import { Badge } from "../components/ui/badge";
import { MerchantLogo } from "../components/MerchantLogo";
import EditTransactionDialog, { CATEGORY_GROUPS } from "../components/EditTransactionDialog";

const fmt = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n || 0);

const Transactions = () => {
    const { language } = useApp();
    const [txs, setTxs] = useState([]);
    const [q, setQ] = useState("");
    const [cat, setCat] = useState("all");
    const [editing, setEditing] = useState(null);

    useEffect(() => {
        const load = async () => {
            const res = await api.get("/transactions", { params: cat !== "all" ? { category: cat } : {} });
            setTxs(res.data);
        };
        load();
    }, [cat]);

    const filtered = txs.filter((tx) => {
        if (!q) return true;
        const name = (tx.merchant_name || tx.name || "").toLowerCase();
        return name.includes(q.toLowerCase());
    });

    const onSaved = (updated) => {
        setTxs((cur) => cur.map((x) => (x.transaction_id === updated.transaction_id ? { ...x, ...updated } : x)));
    };

    return (
        <AppLayout title={t(language, "nav_transactions")}>
            <div className="mb-6 flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))]" />
                    <Input
                        data-testid="transactions-search"
                        placeholder={language === "es" ? "Buscar comercio..." : "Search merchant..."}
                        className="pl-10 pill h-11"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                    />
                </div>
                <Select value={cat} onValueChange={setCat}>
                    <SelectTrigger className="w-full sm:w-56 pill h-11" data-testid="category-select">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="max-h-[380px]">
                        <SelectItem value="all">{t(language, "all_categories")}</SelectItem>
                        {CATEGORY_GROUPS.map((g) => (
                            <div key={g.label_en}>
                                <div className="px-2 py-1.5 text-[10px] font-bold tracking-widest uppercase text-[hsl(var(--muted-foreground))]">
                                    {language === "es" ? g.label_es : g.label_en}
                                </div>
                                {g.items.map((c) => (
                                    <SelectItem key={c} value={c}>{t(language, `cat_${c}`)}</SelectItem>
                                ))}
                            </div>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            <Card className="rounded-3xl border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-sm">
                <CardContent className="p-3 md:p-4">
                    {filtered.length === 0 ? (
                        <div className="p-10 text-center text-[hsl(var(--muted-foreground))]">{t(language, "no_data")}</div>
                    ) : (
                        <div className="flex flex-col gap-2">
                            {filtered.map((tx) => {
                                const isIncome = tx.amount < 0; // Plaid convention: negative = money in
                                const emoji = isIncome ? "↗" : "↘";
                                const amountColor = isIncome ? "text-[#0AFFB0]" : "text-[hsl(var(--foreground))]";
                                const iconBg = isIncome
                                    ? "bg-[#0AFFB0]/12 text-[#0AFFB0]"
                                    : "bg-[#FF3EA5]/12 text-[#FF3EA5]";
                                return (
                                    <button
                                        key={tx.transaction_id}
                                        type="button"
                                        onClick={() => setEditing(tx)}
                                        data-testid="transaction-row"
                                        className="group w-full text-left flex items-center gap-3 px-4 md:px-5 py-3.5 rounded-2xl border border-transparent hover:border-[hsl(var(--primary))]/30 hover:bg-[hsl(var(--secondary))]/50 transition-all active:scale-[0.995] cursor-pointer"
                                    >
                                        <MerchantLogo
                                            merchant={tx.merchant_name || tx.name || ""}
                                            size={40}
                                            className={`shrink-0 ${iconBg}`}
                                        />
                                        <div className="flex-1 min-w-0">
                                            <div className="font-semibold text-[hsl(var(--foreground))] truncate">
                                                {tx.merchant_name || tx.name}
                                            </div>
                                            <div className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5 flex items-center gap-2 flex-wrap">
                                                <span className="tabular-nums">{tx.date?.slice(0, 10)}</span>
                                                <Badge
                                                    variant="outline"
                                                    className="text-[10px] font-semibold tracking-wide border-[hsl(var(--primary))]/25 bg-[hsl(var(--primary))]/8 text-[hsl(var(--foreground))]"
                                                >
                                                    {t(language, `cat_${tx.app_category || "other"}`)}
                                                </Badge>
                                                {tx.note ? (
                                                    <span className="text-[hsl(var(--muted-foreground))] italic truncate max-w-[200px]">
                                                        "{tx.note}"
                                                    </span>
                                                ) : null}
                                            </div>
                                        </div>
                                        <div className={`font-display font-bold text-lg tabular-nums ${amountColor}`}>
                                            {isIncome ? "+" : "-"}{fmt(Math.abs(tx.amount))}
                                        </div>
                                        <span
                                            aria-hidden
                                            className="p-2 rounded-full text-[hsl(var(--muted-foreground))] group-hover:text-[hsl(var(--primary))] transition-colors"
                                        >
                                            <Pencil className="w-4 h-4" />
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </CardContent>
            </Card>

            <EditTransactionDialog
                open={!!editing}
                onOpenChange={(v) => !v && setEditing(null)}
                tx={editing}
                onSaved={onSaved}
            />
        </AppLayout>
    );
};

export default Transactions;
